import React, {useRef, useState} from 'react'
import {
  ArrowsPointingOutIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  MusicalNoteIcon,
  PaperAirplaneIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";
import {Link} from "react-router";
import {Field} from "../../ui/elements/field";
import {Button} from "../../ui/elements/button";
import * as API from '@/api'
import CushHead from '../../../images/cushhead.png'
import {vlogDateString} from "./index";
import consumer from '../../../channels/consumer'
import {useAuth} from "../../../contexts/auth_context";
import commentsReducer from './comments_reducer'
import {PlayerProvider, usePlayer} from "./player_context";

const usernameColors = [
    "text-[#4FBDAB]",
    "text-[#4F98BD]",
    "text-[#4F61BD]",
    "text-[#BD4F61]",
    "text-[#BDAB4F]"
]

const usernameColorsMap = {}
const getUsernameColor = username => {
  const existing = usernameColorsMap[username]
  if (existing) return existing
  usernameColorsMap[username] = usernameColors[Math.floor(Math.random() * usernameColors.length)];
  return usernameColorsMap[username]
}

const loadScript = (src, attrs, parentNode) => new Promise((resolve, reject) => {
    const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]')
    if (existingScript) {
      existingScript.remove()
    }

    const script = document.createElement('script')
    script.async = false
    script.src = src

    for (const [k, v] of Object.entries(attrs || {})) {
      script.setAttribute(k, v)
    }

    script.onload = () => {
      resolve(script)
    }

    script.onerror = () => {
      reject(new Error(`Failed to load ${src}`))
    }

    const node = parentNode || document.head || document.getElementsByTagName('head')[0]
    node.appendChild(script)
  })


const getCurrentTimestamp = (transcript, time) => {
  return transcript.timestamps.find(timestamp => time >= timestamp.start_seconds &&
    time <= timestamp.finish_seconds
  )
}

const SIZES = {
  medium: 'md:w-2/3',
  large: 'md:w-7/8'
}
const getCurrentComment = (comments, time) => {
  return comments.reverse().find(comment => time >= comment.milliseconds / 1000)
}

const scrollToTimestamp = (timestamp) => {
  const foundEl = document.querySelector(`#cushvlog-timestamp-${timestamp.id}`)
  foundEl.scrollIntoView({ behavior: 'smooth' })
}

const CommentForm = props => {
  const {vlog, replay, player, addComment} = props

  const onSubmit = (evt) => {
    evt.preventDefault()

    const formData = new FormData(evt.target)
    formData.set('milliseconds', player.getCurrentTime() * 1000)
    API.Comments.create(vlog, formData).then(cmt => {
      evt.target.reset()
    })
  }

  return <form onSubmit={onSubmit} className="flex flex-col w-full gap-2">
    <Field type="hidden" name="replay_id" value={replay?.id || ''}/>
    <Field type="text" name='body' className="w-full border border-red-400 p-2" />
    <Button type="submit" className="cursor-pointer group justify-end flex items-center self-end gap-1 w-fit py-1 px-2" size="xs">
      Send
      <PaperAirplaneIcon className="w-7 group-hover:text-[#4F98BD] group-active:scale-85 transition-all" />
    </Button>
  </form>
}

const Comments = props => {
  const { vlog, comments, addComment } = props
  const { user } = useAuth()
  const commentsRef = useRef()
  const {youtubePlayer} = usePlayer()
  const [currentComment, setCurrentComment] = React.useState(null)

  React.useEffect(() => {
    if (!youtubePlayer.getPlayerState) return

    let interval;

    if (youtubePlayer.getPlayerState() === YT.PlayerState.PLAYING) {
      const interval = setInterval(
        () => {
          const newCurrentComment = getCurrentComment(comments, youtubePlayer.getCurrentTime())
          if (newCurrentComment?.id === currentComment?.id) return;
          setCurrentComment(newCurrentComment)
        },
        200
      )
    }

    return () => {
      clearInterval(interval)
    }
  }, [])

  return <ul className="text-sm flex flex-col gap-2 overflow-scroll h-full pb-4" ref={commentsRef}>
    {comments.map((comment, i) => <li key={comment.id}
                                      className={`md:leading-5 tracking-wider gap-1 ${currentComment?.id === comment.id && 'inline'}`}>
      <span className="mr-1 "><Link to={`/users/${null}`} className={`font-bold ${getUsernameColor(comment.author.id)}`}>{comment.author.username}</Link></span>
      <span>{comment.body}</span>
    </li>)}
  </ul>
}

const Chat = props => {
  const {vlog, replay, player, comments, addComment} = props

  return <div className="w-full h-full backdrop-blur-lg p-4 flex flex-col font-[Axiforma_Book]">
    {comments.length > 0 && <Comments comments={comments} vlog={vlog} addComment={addComment} />}
    <div className="w-full text-sm flex items-center gap-1 sticky bottom-0">
      <CommentForm vlog={vlog} replay={replay} player={player} addComment={addComment}/>
    </div>
  </div>
}

const Scrubber = props => {
  const { vlog, player } = props
  const [currentMilliseconds, setCurrentMilliseconds] = useState(0)

  React.useEffect(() => {
    if (!player || !player.getCurrentTime) return

    const interval = setInterval(() => {
      if (player.getPlayerState() !== YT.PlayerState.PLAYING) return
      setCurrentMilliseconds(player.getCurrentTime() * 1000)
    }, 200)
  }, [player])

  const scrubberWidthPercentage= currentMilliseconds / vlog.duration_milliseconds

  const timestampParts = []
  const seconds = Math.floor(currentMilliseconds / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  if (hours > 0) {
    timestampParts.push(hours)
  }
  timestampParts.push(String(minutes % 60).padStart(2, '0'))
  timestampParts.push(String(seconds % 60).padStart(2, '0'))
  const timestamp = timestampParts.join(":")


  return <div className="bg-zinc-800 w-full relative h-4">
    <div className="absolute flex flex-col items-center top-1/2 -translate-y-1/2" style={{ left: `${scrubberWidthPercentage * 100}%` }}>
      <div className="text-slate-300 absolute bottom-6">{timestamp}</div>
      <div className="bg-[#4F98BD] rounded-full size-6" />
    </div>
  </div>
}

export const VlogPlayer = props => {
  const {vlog, transcript, replay = null} = props
  const [playerState, setPlayerState] = React.useState(null)
  const [player, setPlayer] = React.useState(null)
  const curtainRef = React.useRef(null)
  const [ready, setReady] = React.useState(false)
  const iframeRef = React.useRef(null)
  const playerRef = React.useRef(null)
  const [size, setSize] = React.useState('medium')
  const [currentTimestamp, setCurrentTimestamp] = React.useState(null)
  const [showCaptions, setShowCaptions] = React.useState(true)
  const { user } = useAuth()
  const [showChat, setShowChat] = React.useState(false)
  const [isLandscape, setIsLandscape] = React.useState(screen.orientation.type.includes('landscape'))
  const [state, dispatch] = React.useReducer(commentsReducer, [])
  const comments = state

  const setComments = newComments => {
    dispatch({ type: 'SET_COMMENTS', payload: newComments})
  }

  const addComment = newComment => {
    dispatch({ type: 'ADD_COMMENT', payload: newComment})
  }

  const loadPlayer = () => {
    window.onYouTubeIframeAPIReady = () => {
      const newPlayer = new window.YT.Player('cushvlog-player', {
        width: window.visualViewport.width,
        height: window.visualViewport.height
      })
      newPlayer.addEventListener('onReady', () => {
        setReady(true)
        newPlayer.seekTo(0.1)
        newPlayer.addEventListener('onStateChange', evt => setPlayerState(evt.data))
      })

      setPlayer(newPlayer)
    }

    loadScript('https://www.youtube.com/iframe_api')
  }

  React.useEffect(() => {
    const listener = (event) => {
      setIsLandscape(event.target.type.includes('landscape'))
    }
    screen.orientation.addEventListener("change", listener);

    return () => screen.orientation.removeEventListener("change", listener);
  }, [])

  React.useEffect(() => {
    const subscription = consumer.subscriptions.create(
      { channel: "VlogChannel", vlog_id: vlog.id },
      {
        received(payload) {
          let comment = payload;

          if (typeof payload === 'string') {
            comment = JSON.parse(payload)
          }

          addComment(comment)
        }
      })

    if (replay) {
      API.Replays.comments(replay).then(setComments)
    } else {
      API.Comments.query(vlog).then(setComments)
    }

    return () => subscription.unsubscribe()
  }, []);

  React.useEffect(() => {
    if (!player) {
      loadPlayer()
    }

    return () => {
      if (player) player.destroy()
      delete window.onYouTubeIframeAPIReady
      Array.from(document.querySelectorAll('script[src="https://www.youtube.com/iframe_api"]')).forEach((el) => el.remove())
    }
  }, [player]);

  React.useEffect(() => {
    if (!ready || !player.getPlayerState) return

    let interval;

    if (player.getPlayerState() === YT.PlayerState.PLAYING) {
      const interval = setInterval(
        () => {
          const newCurrentTimestamp = getCurrentTimestamp(transcript, player.getCurrentTime())
          setCurrentTimestamp(newCurrentTimestamp)
        },
        200
      )
    }

    return () => {
      clearInterval(interval)
    }
  }, [currentTimestamp?.id, ready, player?.getPlayerState ? player.getPlayerState() : null])

  React.useEffect(() => {
    curtainRef.current.addEventListener('mousemove', onMouseMove)

    return () => {
      if (!curtainRef.current) return
      curtainRef.current.removeEventListener('mousemove', onMouseMove)
    }
  }, [curtainRef.current])

  const togglePlay = (evt) => {
    if (!player?.getPlayerState) {
      loadPlayer()
      return
    }
    if (!ready) return

    const clickEls = ['A', 'BUTTON']
    if (clickEls.includes(evt.target.tagName) || clickEls.some(tagName => evt.target.closest(tagName))) {
      return
    }

    if (player.getPlayerState() === YT.PlayerState.PLAYING) {
      player.pauseVideo()
    }
    else {
      player.playVideo()
    }
  }

  const removeCurtain = () => {
    if (!curtainRef.current) return

    curtainRef.current.classList.remove('opacity-75')
    curtainRef.current.classList.add('opacity-0')
    curtainRef.current.classList.remove('hover:cursor-auto')
    curtainRef.current.classList.remove('hover:cursor-pointer')
    curtainRef.current.classList.add('hover:cursor-none')
  }

  const showCurtain = () => {
    curtainRef.current.classList.remove('opacity-0')
    curtainRef.current.classList.add('opacity-75')
    curtainRef.current.classList.remove('hover:cursor-none')
    curtainRef.current.classList.add('hover:cursor-auto')
    const hideDelay = setTimeout(removeCurtain, 4000)

    return () => clearTimeout(hideDelay)
  }

  const onMouseMove = () => {
    if (!curtainRef.current.classList.contains('opacity-75')) {
      showCurtain()
    }
  }

  const toggleFullScreen = () => {
    if (!playerRef.current) return

    console.log('fullscreen')
  }

  return <PlayerProvider youtubePlayer={player}>
    <div className={`fixed w-dvw h-dvh mx-auto flex bg-slate-950`} ref={playerRef}>
    <div className="w-full h-full relative hover:cursor-pointer transition-all bg-transparent">
      <div className={`text-sm md:text-xl rounded-lg w-full relative h-full flex flex-col group`} >
        <div className="z-20 top-0 left-0 absolute w-full h-full" onMouseMove={onMouseMove} onClick={togglePlay} ref={curtainRef}>
          <div className="absolute top-0 left-0 flex flex-col gap-2 bg-linear-to-b/shorter  from-black to-black/60 py-4 w-full px-4 lg:px-12 pt-14 ">
            <div className="flex items-start gap-4 w-full">
              <div className="">
                <Link to='/'>
                  <img src={CushHead} className="w-16" />
                </Link>
              </div>
              <div className="flex flex-col gap-1">
                <h2 className="text-lg md:text-2xl font-[Axiforma_Black]">
                  {vlog.title}
                </h2>
                <div className=" italic flex items-center gap-2 w-fit">
                  <CalendarDaysIcon className="size-5 -translate-y-0.5" />
                  {vlog.recorded_on_epoch_milliseconds && <span>{vlogDateString(vlog)}</span>}
                </div>
                {/*<div className="flex items-center gap-2 w-fit">*/}
                {/*  <PlayCircleIcon className="w-5 -translate-y-0.5" />*/}
                {/*  {vlog.duration_string}*/}
                {/*</div>*/}
                {vlog.croon && <div className="flex items-center gap-2 w-fit">
                  <MusicalNoteIcon className="w-5 -translate-y-0.5" />
                  {vlog.croon}
                </div>}
                {/*<div>*/}
                {/*  <Link to={`/vlogs/${vlog.id}/edit`} className="flex items-center gap-2 w-fit">*/}
                {/*    <PencilIcon className="w-5 -translate-y-0.5" />*/}
                {/*    Edit*/}
                {/*  </Link>*/}
                {/*</div>*/}
              </div>
            </div>
          </div>
          <div className="absolute py-6 left-0 bottom-0 lg:pb-6 flex gap-4 items-center w-full bg-linear-to-t/longer from-black to-black/60">
            <div>
              {ready && (player.getPlayerState() === YT.PlayerState.PLAYING ? <PauseCircleIcon className="w-12 stroke-1 hover:cursor-pointer hover:fill-blue-600" /> : <PlayCircleIcon className="w-12 stroke-1 hover:cursor-pointer hover:fill-blue-600" />)}
            </div>
            <div className="w-full">
              {player && player.getCurrentTime && <Scrubber vlog={vlog} player={player} />}
            </div>
            <div className="ml-auto flex items-center gap-6 w-fit">
              <button type="button" onClick={() => setShowCaptions((!showCaptions))} className={`py-1 px-2 cursor-pointer font-mono text-2xl ${showCaptions && 'bg-white/20'}`}>
                CC
              </button>
              <button type="button" onClick={toggleFullScreen} className="cursor-pointer">
                <ArrowsPointingOutIcon className="w-6" />
              </button>
            </div>
          </div>
          {isLandscape && user && !showChat && <div className="absolute top-6 right-6 md:pt-10 md:pr-12">
            <button type="button" className="flex items-center gap-2 bg-white/20 rounded-lg px-3 py-2 hover:cursor-pointer hover:bg-black/20" onClick={() => setShowChat(!showChat)}>
              <ChatBubbleLeftRightIcon className="size-6" />
            </button>
          </div>}
        </div>
        <div className="w-full h-full flex flex-col justify-center items-center">
          <iframe
            ref={iframeRef}
            id="cushvlog-player"
            className={`w-full h-full pt-10 rounded-lg`}
            src={`https://www.youtube.com/embed/${vlog.youtube_slug}?enablejsapi=1&controls=0&rel=0&seekTo=1`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen={0}
          />
          {showCaptions && currentTimestamp && <div className="absolute w-full bottom-20 left-6 flex justify-center text-center">
            <div className="">
              <span className="bg-zinc-900/40 px-2 py-3 md:text-2xl inline">{currentTimestamp.transcription}</span>
            </div>
          </div>}
        </div>
      </div>
    </div>
    {showChat && <div className="w-2/3 lg:w-1/3 h-dvh bg-zinc-900 text-white border-l border-zinc-600 items-start flex flex-col">
      <div className="flex w-full justify-between items-center px-4 pt-6">
        <div>Chat</div>
        <button type="button" className="hover:cursor-pointer w-full" onClick={() => setShowChat(!showChat)}>
          <XMarkIcon className="size-5 ml-auto" />
        </button>
      </div>
      <Chat vlog={vlog} replay={replay} player={player} comments={comments} addComment={addComment}/>
    </div>}
  </div>
  </PlayerProvider>
}